import { useQuery } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys } from '@api/queryClient';

// ============================================================================
// Types
// ============================================================================

export interface FlowMeterComponent {
  id: string;
  tankId: string;
  tankName: string;
  weight: number;
  temperature: number;
  bookkeepingEntryId: string;
}

export interface FlowMeter {
  id: string;
  receivedAt: string;
  originalJsonData: string;
  terminalId: string;
  terminalName: string;
  productMixtureId: string;
  productCode: string;
  productName: string;
  startDate: string;
  endDate: string;
  totalWeight: number;
  register: string;
  type: FlowMeterProductType;
  components: FlowMeterComponent[];
}

export interface FlowMeterHydrogenPeroxide {
  id: string;
  bookkeepingEntryId: string;
  receivedAt: string;
  originalJsonData: string;
  tankId: string;
  tankName: string;
  endDate: string;
  totalWeight: number;
  waterWeight: number;
  hydrogenPeroxideWeight: number;
  concentrationPercent: number;
  register: string;
  waybillNumber: string;
}

export enum FlowMeterProductType {
  Unknown = 0,
  SingleTankProduct = 1,
  ProductMixture = 2,
  UnknownProductMixture = 3
}

export interface FetchFlowMetersParams {
  from: string;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'flowMeters';

const fetchFlowMeters = async (from: string): Promise<FlowMeter[]> => {
  const queryParams = `from=${from || ''}`;
  return apiRequest<void, FlowMeter[]>(HttpMethod.GET, `${API_ENDPOINT}?${queryParams}`);
};

const fetchFlowMetersHydrogenPeroxide = async (from: string): Promise<FlowMeterHydrogenPeroxide[]> => {
  const queryParams = `from=${from || ''}`;
  return apiRequest<void, FlowMeterHydrogenPeroxide[]>(HttpMethod.GET, `${API_ENDPOINT}/hydrogenperoxide?${queryParams}`);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch flow meters with date filter
 */
export const useFlowMetersQuery = (from: string) => {
  return useQuery({
    queryKey: queryKeys.flowMeters.list({ from }),
    queryFn: () => fetchFlowMeters(from),
  });
};

/**
 * Fetch hydrogen peroxide flow meters with date filter
 */
export const useFlowMetersHydrogenPeroxideQuery = (from: string) => {
  return useQuery({
    queryKey: [...queryKeys.flowMeters.all, 'hydrogenPeroxide', { from }],
    queryFn: () => fetchFlowMetersHydrogenPeroxide(from),
  });
};
